import './Input.scss';
import {
    forwardRef,
    useId,
    ComponentProps,
    HTMLAttributes,
    InputHTMLAttributes,
    ReactNode,
} from 'react';
import classNames from 'classnames';
import { FormHelperText } from '@mui/material';
import { ErrorWidget } from 'app/agriculture/error_widget';
import { InputAdornment } from 'app/agriculture/forms/components/Input/InputAdornment';
import { InputLabel } from 'app/agriculture/forms/components/InputLabel/InputLabel';
import { Popover } from 'app/agriculture/components/Popover/Popover';

interface InputProps {
    className?: string;
    label: string;
    labelProps?: HTMLAttributes<HTMLLabelElement>;
    // These are the types that have styles implemented, if you add more you might need to add to the styles
    type?: 'text' | 'number' | 'time';
    errorMessage?: string;
    adornment?: ComponentProps<typeof InputAdornment>;
    placeholder?: string;
    step?: number | string;
    min?: number | string;
    defaultValue?: number | string;
    value?: number | string;
    focusPopover?: ReactNode;
}

export const Input = forwardRef<
    HTMLInputElement,
    InputHTMLAttributes<HTMLInputElement> & InputProps
>(function Input(
    { className, label, labelProps, errorMessage, adornment, focusPopover, ...inputBaseProps },
    ref,
) {
    const labelId = useId();

    const input = (
        <input
            {...inputBaseProps}
            aria-labelledby={labelId}
            className={classNames('Input__input', className)}
            ref={ref}
        />
    );

    return (
        <div className={classNames('Input', { 'Input--with-adornment': !!adornment })}>
            <InputLabel id={labelId} className="Input__label" {...labelProps}>
                {label}
            </InputLabel>
            {focusPopover == null ? (
                input
            ) : (
                <Popover triggerInteractions={['focus']}>
                    <Popover.Trigger>
                        <div>{input}</div>
                    </Popover.Trigger>
                    <Popover.Content>{focusPopover}</Popover.Content>
                </Popover>
            )}
            {!adornment ? null : <InputAdornment {...adornment} />}
            {errorMessage == null ? null : (
                <FormHelperText className="Input__error" error={true}>
                    <ErrorWidget errorMessage={errorMessage} />
                </FormHelperText>
            )}
        </div>
    );
});
